import { AbstractControl, ValidatorFn } from "@angular/forms";
import { IMenu, ISubMenu } from "./models/roles/access-permission";
import { IRole, IRoleName } from "./models/roles/role";
import { IInvoiceParts, IInvoiceResponce } from "./models/invoice/invoiceResp";
import { ICompany } from "./models/companies/company";
import { NumberToWordsPipe } from 'src/app/pipes/number-to-words.pipe';
import { saveAs } from 'file-saver';
import { SafeHtml } from "@angular/platform-browser";

// This file used for common methods.
export class CommonMethods {

     /**
     * Extract base price from GST-inclusive amount
     */
    static getBasePrice(totalWithGst: number, gstRate: number): number {
        return gstRate > 0 ? totalWithGst / (1 + gstRate / 100) : totalWithGst;
    }

        /** 
     * Returns a display-ready label based on selectedDateFilter
     * daily   → YYYY-MM-DD (today)
     * monthly → MonthNameYear (e.g. October2025)
     * weekly  → startDate to endDate
     * custom  → customStart to customEnd
     */
    static getDateLabel(selectedDateFilter: string, dateRange: any, customStart?: string, customEnd?: string): string {

    // DAILY → today's local date
    if (selectedDateFilter === 'daily') {
        return dateRange.startDate;
    }

    // MONTHLY → MonthNameYear
    if (selectedDateFilter === 'monthly') {
        const d = new Date();
        const months = [
        'January','February','March','April','May','June',
        'July','August','September','October','November','December'
        ];
        return `${months[d.getMonth()]}-${d.getFullYear()}`;  
    }

    // WEEKLY
    if (selectedDateFilter === 'weekly') {
        return `${dateRange.startDate} to ${dateRange.endDate}`;
    }

    // CUSTOM
    if (selectedDateFilter === 'custom') {
        return `${customStart || dateRange.startDate} to ${customEnd || dateRange.endDate}`;
    }

    // FALLBACK (same as weekly/monthly combined range)
    return `${dateRange.startDate} to ${dateRange.endDate}`;
    }


    /**
     * Calculate GST after applying discount before tax
     */
    static calculateDiscountedGST(
        parts: any[],
        totalPartPrice: number,
        discount: number,
    ): number {
        const discountedBase = Math.max(totalPartPrice - discount, 0);
        let totalGST = 0;

        for (const part of parts) {
        const totalWithGst = parseFloat(part.totalWithGst ?? '0');
        const gstRate = parseFloat(part.gst?.gst_type ?? '0') || 0;

        if (gstRate > 0) {
            const basePrice = this.getBasePrice(totalWithGst, gstRate);
            const share = totalPartPrice > 0 ? basePrice / totalPartPrice : 0;
            const discountedBaseForItem = discountedBase * share;

            totalGST += (discountedBaseForItem * gstRate) / 100;
        }
        }

        return +totalGST;
    }

    /**
     * Calculate final amount with GST + charges + adjustments
     */
    static calculateFinalAmount(
        totalPartPrice: number,
        discount: number,
        gstAmount: number,
        charges: {
        serviceCharges?: number;
        serviceChargesR?: number;
        roundOffSub?: number;
        roundOffSum?: number;
        freightForwarding?: number;
        igstExportSez?: number;
        }
    ): number {
        const discountedBase = Math.max(totalPartPrice - discount, 0);

        const {
        serviceCharges = 0,
        serviceChargesR = 0,
        roundOffSub = 0,
        roundOffSum = 0,
        freightForwarding = 0,
        igstExportSez = 0,
        } = charges;

        const grandTotal =
        discountedBase +
        gstAmount +
        serviceCharges +
        serviceChargesR +
        roundOffSum +
        freightForwarding +
        igstExportSez -
        roundOffSub;

        return +grandTotal;
    }

    public static getItem(item: any) {
        return localStorage.getItem(item);
    }

    public static setItem(itemName: any, itemValue: any) {
        localStorage.setItem(itemName, itemValue);
    }

    public static removeItem(item: any) {
        localStorage.removeItem(item);
    }

    /**
     * Formats an address string by splitting it on <br> tags,
     * trimming each part, converting it to title case,
     * and joining them back with <br> for safe HTML display.
     *
     * @param address The raw address string possibly containing <br> tags.
     * @returns A formatted HTML-safe string with line breaks and title-cased parts.
     */
    public static splitAddressFromBR(address: any): string {
        const raw = address ?? '';
        return raw
            .split('<br>')
            .map((part: string) => part.trim().charAt(0).toUpperCase() + part.trim().slice(1))
            .join('<br>');
    }

    static formatLocalYYYYMMDD(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    static  getTotalCostWithoutGst(invoice: any): number {
        return invoice?.parts_details?.reduce((total: any, part: any) => {
          return total + CommonMethods.getPartTotal(part);
        }, 0) || 0
    }

    static getPartTotal(part: any): number {
        const price = Number(part?.sell_price) || 0;
        const qty = Number(part?.quantity) || 0;
        return price * qty;
    }

    static  getTerms(company: any): SafeHtml {
      if (company?.terms_and_conditions) {
        const formatted = company.terms_and_conditions
          .replace(/(\r\n|\n|\r)/g, '<br>');
        return formatted;
      }
      return 'NA';
    }

    static getTaxableAmount(part: any): number {
      const cgst = parseFloat(part?.CGST_amount || 0);
      const sgst = parseFloat(part?.SGST_amount || 0);
      const igst = parseFloat(part?.IGST_amount || 0);
      return cgst + sgst + igst;
    }

    static async exportInvoice(invoice: IInvoiceResponce, company: ICompany, getItemTotal: any, lastItemCgst: any, lastItemSgst: any, getTotalCGST: any, getTotalSGST: any, getTotalQuantity: any, getTotalGST: any, getItemsTotal: any, getTotalCGSTRate: any, getTotalSGSTRate: any) {
      const inv = invoice;
      if (!inv) {
        console.error('Invoice data not found');
        return;
      }
    
      const ExcelJS = (await import('exceljs/dist/exceljs.min.js')) as any;
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Invoice');
    
        const thinBorder = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
    
        const mergeBorder = (existing: any, next: any) => ({
          top: next?.top ?? existing?.top,
          left: next?.left ?? existing?.left,
          bottom: next?.bottom ?? existing?.bottom,
          right: next?.right ?? existing?.right
        });
    
        const setBorderRange = (r1: number, c1: number, r2: number, c2: number) => {
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
              ws.getCell(r, c).border = thinBorder;
            }
          }
        };
    
        const addMergedVerticalDivider = (r1: number, r2: number, leftMasterCol: number, rightMasterCol: number) => {
          for (let r = r1; r <= r2; r++) {
            const leftMaster = ws.getCell(r, leftMasterCol);
            leftMaster.border = mergeBorder(leftMaster.border, { right: { style: 'thin' } });
    
            const rightMaster = ws.getCell(r, rightMasterCol);
            rightMaster.border = mergeBorder(rightMaster.border, { left: { style: 'thin' } });
          }
        };
    
        const setOuterBorderRange = (r1: number, c1: number, r2: number, c2: number) => {
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
              ws.getCell(r, c).border = {};
            }
          }
    
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
              const cell = ws.getCell(r, c);
              const next: any = {};
              if (r === r1) next.top = { style: 'thin' };
              if (r === r2) next.bottom = { style: 'thin' };
              if (c === c1) next.left = { style: 'thin' };
              if (c === c2) next.right = { style: 'thin' };
              cell.border = mergeBorder(cell.border, next);
            }
          }
        };
    
        const clearRowBorderSide = (r: number, side: 'top' | 'bottom') => {
          for (let c = 1; c <= 10; c++) {
            const cell = ws.getCell(r, c);
            const b: any = cell.border || {};
            cell.border = {
              top: b.top,
              left: b.left,
              right: b.right,
              bottom: b.bottom,
              [side]: undefined
            };
          }
        };
    
        const applyCell = (
          row: number,
          col: number,
          value: any,
          opts?: {
            bold?: boolean;
            align?: 'left' | 'center' | 'right';
            valign?: 'top' | 'middle' | 'bottom';
            wrap?: boolean;
            numFmt?: string;
            fontSize?: number;
          }
        ) => {
          const cell = ws.getCell(row, col);
          cell.value = value;
          cell.font = {
            name: 'Arial',
            size: opts?.fontSize ?? 11,
            bold: !!opts?.bold,
            color: { argb: 'FF000000' }
          };
          cell.alignment = {
            horizontal: opts?.align ?? 'left',
            vertical: opts?.valign ?? 'top',
            wrapText: opts?.wrap ?? true
          };
          if (opts?.numFmt) cell.numFmt = opts.numFmt;
          return cell;
        };
    
        ws.getColumn(1).width = 6;
        ws.getColumn(2).width = 34;
        ws.getColumn(3).width = 16;
        ws.getColumn(4).width = 10;
        ws.getColumn(5).width = 14;
        ws.getColumn(6).width = 14;
        ws.getColumn(7).width = 14;
        ws.getColumn(8).width = 14;
        ws.getColumn(9).width = 14;
        ws.getColumn(10).width = 16;
    
        ws.getRow(1).height = 24;
        ws.mergeCells('A1:J1');
        applyCell(1, 1, 'Invoice', { bold: true, align: 'center', valign: 'middle', wrap: false, fontSize: 16 });
        setBorderRange(1, 1, 1, 10);
    
        const setRowBoxWithMiddle = (r: number) => {
          for (let c = 1; c <= 10; c++) {
            ws.getCell(r, c).border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },

              left: c === 1 ? { style: 'thin' } : undefined,

              right:
                c === 5
                  ? { style: 'thin' }
                  : c === 10
                  ? { style: 'thin' }
                  : undefined
            };
          }
        };
    
        // ROW 2–4: Company + Invoice Info
        ws.mergeCells('A2:E4');
        ws.mergeCells('F2:G4');
        ws.mergeCells('H2:J4');
    
        const companyCell = ws.getCell(2, 1);
    
        companyCell.value = {
          richText: [
            {
              text: `${company?.name || ''}\n`,
              font: { bold: true }
            },
            {
              text: `Address: ${company?.address || ''}\n`
            },
            {
              text: `GSTIN/UIN: ${company?.gstin || ''}\n`
            },
            {
              text: `Email: ${company?.email_id || ''}\n`
            },
            {
              text: `Phone No: ${company?.mobile_number || ''}`
            }
          ]
        };
    
        companyCell.font = {
          name: 'Arial',
          size: 11,
          color: { argb: 'FF000000' }
        };
    
        companyCell.alignment = {
          vertical: 'top',
          wrapText: true
        };
    
    
        const invoiceNoCell = ws.getCell(2, 6);
        invoiceNoCell.value = `Invoice No.\n${inv.bill_no || ''}`;
        invoiceNoCell.font = {
        name: 'Arial',
        size: 11,
        color: { argb: 'FF000000' },
        bold: false
        };
        invoiceNoCell.alignment = {
        vertical: 'top',
        wrapText: true
        };

        const datedCell = ws.getCell(2, 8);
        const formattedDate = inv.created_at
        ? new Date(inv.created_at as any).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
            })
        : '';
        datedCell.value = `Dated\n${formattedDate}`;
        datedCell.font = {
        name: 'Arial',
        size: 11,
        color: { argb: 'FF000000' },
        bold: false
        };
        datedCell.alignment = {
        vertical: 'top',
        wrapText: true
        };

    
        ws.getRow(2).height = 30;
        ws.getRow(3).height = 30;
        ws.getRow(4).height = 30;
        ws.getRow(5).height = 30;
        ws.getRow(6).height = 30;
        ws.getRow(7).height = 30;
    
        setRowBoxWithMiddle(2);
        setRowBoxWithMiddle(3);
        setRowBoxWithMiddle(4);
        addMergedVerticalDivider(2, 4, 1, 6);
        addMergedVerticalDivider(2, 4, 6, 8);
    
        // ROW 5–10: Customer Details + Terms
        ws.mergeCells('A5:E10');
        ws.mergeCells('F5:J10');
    
        const cust = inv.customer_details as any;
        
        const customerCell = ws.getCell(5, 1);
        customerCell.value = {
          richText: [
            {
              text: 'Customer Details\n',
              font: { bold: true }
            },
            {
              text: `${cust?.name || ''}\n`,
              font: { bold: true }
            },
            {
              text: `${cust?.address || ''}\n`
            },
            {
              text: `GSTIN/UIN: ${cust?.gstin || ''}\n`
            },
            {
              text: `AADHAR/PAN NO: ${cust?.aadhaar_or_pan_no || ''}\n`
            },
            {
              text: `Address: ${cust?.address || ''}`
            }
          ]
        };
        
        customerCell.font = {
          name: 'Arial',
          size: 11,
          color: { argb: 'FF000000' }
        };
        
        customerCell.alignment = {
          vertical: 'top',
          wrapText: true
        };
        
        const termsText = (company?.terms_and_conditions || '').toString().trim();
        const termsCell = ws.getCell(5, 6);
        
        if (termsText) {
            termsCell.value = `Terms of Delivery\n${termsText}`;
        } else {
            termsCell.value = 'Terms of Delivery';
        }

        termsCell.font = {
        name: 'Arial',
        size: 11,
        color: { argb: 'FF000000' }
        };

        termsCell.alignment = {
        vertical: 'top',
        wrapText: true
        };
        
        setRowBoxWithMiddle(5);
        setRowBoxWithMiddle(6);
        setRowBoxWithMiddle(7);
        setRowBoxWithMiddle(8);
        setRowBoxWithMiddle(9);
        setRowBoxWithMiddle(10);
        addMergedVerticalDivider(5, 10, 1, 6);
    
        const headerRow = 11;
        ws.getRow(headerRow).height = 18;
        const hdr = ['S.N.', 'Description of Goods', 'HSN/SAC code', 'Qty', 'Price', 'CGST Amount', 'SGST Amount', 'IGST Amount', 'Total Tax Amount', 'Amount'];
        hdr.forEach((h, idx) => {
          const col = idx + 1;
          const cell = applyCell(headerRow, col, h, { bold: true, align: 'center', valign: 'middle', wrap: true });
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
          cell.border = thinBorder;
        });
    
        let row = headerRow + 1;
        const parts = (inv.parts_details || []) as any[];
        parts.forEach((p, i) => {
          ws.getRow(row).height = 18;
          applyCell(row, 1, i + 1, { align: 'center', wrap: false });
          applyCell(row, 2, p?.name || '', { bold: true, align: 'left' });
          applyCell(row, 3, p?.HSN || 'NA', { align: 'center', wrap: false });
          applyCell(row, 4, Number(p?.quantity || 0), { bold: true, align: 'center', wrap: false, numFmt: '#,##0' });
          applyCell(row, 5, getItemTotal(p), { align: 'right', wrap: false, numFmt: '#,##0.00' });
          applyCell(row, 6, Number(p?.CGST_amount || 0), { align: 'right', wrap: false, numFmt: '#,##0.00' });
          if (p?.CGST) {
            const cgstCell = ws.getCell(row, 6);
            cgstCell.value = {
              richText: [
                { text: `${Number(p?.CGST_amount || 0).toFixed(2)}` },
                { text: `\n${p.CGST}%`, font: { size: 9 } }
              ]
            };
          }
          
          applyCell(row, 7, Number(p?.SGST_amount || 0), { align: 'right', wrap: false, numFmt: '#,##0.00' });
          if (p?.SGST) {
            const sgstCell = ws.getCell(row, 7);
            sgstCell.value = {
              richText: [
                { text: `${Number(p?.SGST_amount || 0).toFixed(2)}` },
                { text: `\n${p.SGST}%`, font: { size: 9 } }
              ]
            };
          }
          
          applyCell(row, 8, Number(p?.IGST_amount || 0), { align: 'right', wrap: false, numFmt: '#,##0.00' });
          if (p?.IGST) {
            const igstCell = ws.getCell(row, 8);
            igstCell.value = {
              richText: [
                { text: `${Number(p?.IGST_amount || 0).toFixed(2)}` },
                { text: `\n${p.IGST}%`, font: { size: 9 } }
              ]
            };
          }
          applyCell(row, 9, CommonMethods.getTaxableAmount(p), { align: 'right', wrap: false, numFmt: '#,##0.00' });
          applyCell(row, 10, Number(p?.grand_total || 0), { bold: true, align: 'right', wrap: false, numFmt: '#,##0.00' });
          setBorderRange(row, 1, row, 10);

          // Remove middle horizontal dividers between item rows
          if (i > 0) clearRowBorderSide(row, 'top');
          if (i < parts.length - 1) clearRowBorderSide(row, 'bottom');
          row++;
        });

        // Also remove the separator line between the last item row and the totals
        if (parts.length > 0) {
          clearRowBorderSide(row - 1, 'bottom');
        }

        if (Number(inv?.discount) > 0) {
          ws.getRow(row).height = 22;
          ws.mergeCells(`A${row}:I${row}`);

          applyCell(row, 1, 'Discount', {
            bold: false,
            align: 'right',
            valign: 'middle',
            wrap: false
          });

          applyCell(row, 10, inv.discount, {
            bold: true,
            align: 'right',
            wrap: false,
            numFmt: '#,##0.00'
          });

          setBorderRange(row, 1, row, 10);
          row++;
        }

        const roundOffValue =
      Number(inv.round_off_sub) > 0
        ? -Number(inv.round_off_sub)
        : Number(inv.round_off_sum) > 0
        ? Number(inv.round_off_sum)
        : 0;

        if (roundOffValue !== 0) {
          ws.getRow(row).height = 22;
          ws.mergeCells(`A${row}:I${row}`);

          applyCell(
            row,
            1,
            `Round off (${roundOffValue > 0 ? '+' : '-'})`,
            { bold: true, align: 'right', wrap: false }
          );

          applyCell(
            row,
            10,
            Math.abs(roundOffValue),
            { bold: true, align: 'right', wrap: false, numFmt: '#,##0.00' }
          );

          setBorderRange(row, 1, row, 10);
          row++;
        }

        const totalQty = getTotalQuantity();
        ws.getRow(row).height = 18;
        ws.mergeCells(`A${row}:C${row}`);
        applyCell(row, 1, 'Total', { bold: false, align: 'right', valign: 'middle', wrap: false });
        applyCell(row, 4, totalQty, { bold: true, align: 'center', wrap: false, numFmt: '#,##0' });
        applyCell(row, 5, getItemsTotal(), { align: 'right', wrap: false, numFmt: '#,##0.00' });
        applyCell(row, 6, getTotalCGST(), { align: 'right', wrap: false, numFmt: '#,##0.00' });
        applyCell(row, 7, getTotalSGST(), { align: 'right', wrap: false, numFmt: '#,##0.00' });
        applyCell(row, 8, 0, { align: 'right', wrap: false, numFmt: '#,##0.00' });
        applyCell(row, 9, getTotalGST(), { align: 'right', wrap: false, numFmt: '#,##0.00' });
        applyCell(row, 10, Number(inv.grand_total || 0), { bold: true, align: 'right', wrap: false, numFmt: '#,##0.00' });
        setBorderRange(row, 1, row, 10);
        row++;

        ws.getRow(row).height = 28;
        ws.mergeCells(`A${row}:I${row}`);
        const numberToWords = new NumberToWordsPipe();
        const amtCell = ws.getCell(row, 1);

        amtCell.value = {
          richText: [
            {
              text: 'Amount chargable (in words)\n',
              font: { bold: true }
            },
            {
              text: `INR ${numberToWords.transform(Number(inv.grand_total || 0))} Only`
            }
          ]
        };

        amtCell.font = {
          name: 'Arial',
          size: 11,
          color: { argb: 'FF000000' }
        };

        amtCell.alignment = {
          vertical: 'top',
          wrapText: true
        };
        applyCell(row, 10, 'E. & OE.', { align: 'right', valign: 'middle', wrap: false });
        setBorderRange(row, 1, row, 10);
        row ++;

        const footerStartRow = row;
        ws.getRow(row).height = 28;
        ws.mergeCells(`A${row}:D${row}`);
        ws.mergeCells(`E${row}:J${row}`);
        const taxWords = `Tax Amount (in words): INR ${numberToWords.transform(Number(getTotalGST() || 0))} Only`;
        const bankCell = ws.getCell(row, 5);

        bankCell.value = {
        richText: [
            {
            text: "Company's Bank Details\n",
            font: { bold: true }
            },
            { text: "Bank Name : " },
            {
            text: `${(company?.bank_name || 'NA').toString()}\n`,
            font: { bold: true }
            },
            { text: "A/c No. : " },
            {
            text: `${(company?.account_number || 'NA').toString()}\n`,
            font: { bold: true }
            },
            { text: "Branch & IFSC Code : " },
            {
            text: `${(company?.branch_name || 'NA').toString()} & ${(company?.ifse_code || 'NA').toString()}`,
            font: { bold: true }
            }
        ]
        };

        bankCell.font = {
        name: 'Arial',
        size: 11,
        color: { argb: 'FF000000' }
        };

        bankCell.alignment = {
        vertical: 'top',
        wrapText: true
        };

        applyCell(row, 1, taxWords, { bold: true, wrap: true });
        row++;

        ws.getRow(row).height = 60;
        ws.mergeCells(`A${row}:D${row}`);
        ws.mergeCells(`E${row}:J${row}`);

        const panDeclCell = ws.getCell(row, 1);

        panDeclCell.value = {
        richText: [
            {
            text: `Company’s PAN : ${(company?.pan_no || 'NA').toString()}\n\n`,
            font: { bold: true }
            },
            {
            text: 'Declaration\n'
            },
            {
            text: 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.'
            }
        ]
        };

        panDeclCell.font = {
        name: 'Arial',
        size: 11,
        color: { argb: 'FF000000' }
        };

        panDeclCell.alignment = {
        vertical: 'top',
        wrapText: true
        };

          const signCell = ws.getCell(row, 5);
    
          signCell.value = {
            richText: [
              {
                text: 'for ',
              },
              {
                text: `${(company?.name || '').toString()}`,
                font: { bold: true }
              },
              {
                text: '\n\n\nAuthorised Signatory'
              }
            ]
          };
    
          signCell.font = {
            name: 'Arial',
            size: 11,
            color: { argb: 'FF000000' }
          };
    
          signCell.alignment = {
            horizontal: 'right',
            vertical: 'top',
            wrapText: true
          };
    
        const footerEndRow = row;
        setOuterBorderRange(footerStartRow, 1, footerEndRow, 10);
    
        const lastRowNumber = ws.lastRow?.number ?? row;
        for (let r = 1; r <= lastRowNumber; r++) {
          const leftCell = ws.getCell(r, 1);
          leftCell.border = mergeBorder(leftCell.border, { left: { style: 'thin' } });
    
          const rightCell = ws.getCell(r, 10);
          rightCell.border = mergeBorder(rightCell.border, { right: { style: 'thin' } });
        }
    
        const buffer = await workbook.xlsx.writeBuffer();
        const safeBillNo = (inv.bill_no || 'Invoice').toString().replace(/[^a-zA-Z0-9_-]/g, '_');
        saveAs(new Blob([buffer]), `${safeBillNo}.xlsx`);
      }

    /**
     * Check applied file input form value's size
     * @param MAX_FILE_SIZE
     * @returns validation
     */
    public static validateImageSize(MAX_FILE_SIZE: number): ValidatorFn {
        return (control: AbstractControl) => {
            const file = control.value;

            if (!file || !(file instanceof File)) {
                return null;
            }

            if (file.size > MAX_FILE_SIZE) {
                return { fileSize: true };
            }

            return null;
        };
    }

    /**
     * Returns the CGST and SGST amounts of the last item in the parts array.
     * @param parts Array of invoice parts
     * @returns Object with cgst and sgst, or null if array is empty
     */
    static getLastItemGST(parts: IInvoiceParts[] | undefined): { cgst: number, sgst: number } | null {
        if (!parts || parts.length === 0) {
        return null;
        }

        const lastItem = parts[parts.length - 1];
        return {
        cgst: Number(lastItem.CGST) || 0,
        sgst: Number(lastItem.SGST) || 0
        };
    }


    // Set the roles and role permissions
    public static ingressSettings: any = [
        {
            role: IRole.ADMIN,
            name: IRoleName.ADMIN,
            permission: [
                IMenu.DASHBOARD,
                IMenu.COMPANY,
            ]
        },
        {
            role: IRole.COMPANY,
            name: IRoleName.COMPANY,
            permission: [
                IMenu.DASHBOARD,
                IMenu.SERVICE_CENTER,
                IMenu.EMPLOYEE,
                IMenu.PART,
                IMenu.VENDOR,
                IMenu.SALE,
                IMenu.ORDER_SUMMARY,
                IMenu.SALE_REPORT,
                IMenu.PURCHASE_REPORT,
                IMenu.CATEGORY,
                IMenu.PART_COMPANY,
                IMenu.CUSTOMER,
                IMenu.INVOICE_UPDATE,
                IMenu.VOUCHER
            ]
        },

        {
            role: IRole.MANAGER,
            name: IRoleName.MANAGER,
            permission: [
                IMenu.DASHBOARD,
                IMenu.ORDER,
                IMenu.PART,
                IMenu.VENDOR,
                IMenu.SALE,
                IMenu.ORDER_SUMMARY,
                IMenu.SALE_REPORT,
                IMenu.PURCHASE_REPORT,
                IMenu.CATEGORY,
                IMenu.PART_COMPANY,
                IMenu.CUSTOMER,
                IMenu.INVOICE_UPDATE,
                IMenu.VOUCHER
            ]
        },
        {
            role: IRole.RECEPTIONIST,
            name: IRoleName.RECEPTIONIST,
            permission: [
                IMenu.DASHBOARD,
                IMenu.ORDER,
                ISubMenu.ORDER_ADD,
                IMenu.PART,
                IMenu.SALE
            ]
        },
        {
            role: IRole.TECHNICIAN,
            name: IRoleName.TECHNICIAN,
            permission: [
                //Dashboard
                IMenu.DASHBOARD,

                //Oders
                IMenu.ORDER,
                ISubMenu.ORDER_ASSIGN,
            ]
        },
        {
            role: IRole.FAVORITE,
            name: IRoleName.FAVORITE,
            permission: [
                IMenu.DASHBOARD,
                IMenu.ORDER_SUMMARY
            ]
        },
    ];
}
